import React, { useState, useMemo } from 'react';
import { useApp } from '../../context/AppContext';
import { formatCurrency, formatDate, today } from '../../utils/helpers';
import { computeBalanceSheet } from '../../utils/financials';
import { downloadElementAsPdf } from '../../utils/exportPdf';
import { FileDown } from 'lucide-react';

const Amt = ({ value, bold }) => (
  <span className={bold ? 'font-bold' : ''} style={{ fontVariantNumeric: 'tabular-nums' }}>
    {formatCurrency(value)}
  </span>
);

export default function BalanceSheet() {
  const { state } = useApp();
  const [asOn, setAsOn] = useState(today());
  const [busy, setBusy] = useState(false);

  const bs = useMemo(() => computeBalanceSheet(state, asOn), [state, asOn]);
  const business = state.company?.name || 'MayaSoft ERP';

  const download = async () => {
    if (busy) return;
    setBusy(true);
    try { await downloadElementAsPdf(document.getElementById('bs-print-area'), `Balance-Sheet-${asOn}`); }
    catch (e) { console.error('Balance Sheet PDF failed:', e); }
    finally { setBusy(false); }
  };

  // Left = Liabilities & Equity; Right = Assets. A reconciling plug is shown on
  // whichever side is short so the two grand totals foot equally.
  const leftRows = [
    { heading: 'Capital & Equity' },
    ...bs.equityRows,
    { heading: 'Liabilities' },
    ...(bs.liabilityRows.length ? bs.liabilityRows : [{ label: 'None', amount: 0, muted: true }]),
    ...(bs.difference > 0 ? [{ label: 'Difference in Opening Balances', amount: bs.difference, muted: true }] : []),
  ];
  const rightRows = [
    { heading: 'Assets' },
    ...(bs.assetRows.length ? bs.assetRows : [{ label: 'None', amount: 0, muted: true }]),
    ...(bs.difference < 0 ? [{ label: 'Difference in Opening Balances', amount: -bs.difference, muted: true }] : []),
  ];

  const Column = ({ title, rows, total }) => (
    <div className="flex-1 min-w-0">
      <div className="bg-gray-800 text-white text-sm font-semibold px-3 py-2 rounded-t">{title}</div>
      <div className="border border-gray-200 border-t-0 rounded-b">
        {rows.map((r, i) => r.heading ? (
          <div key={i} className="bg-gray-50 px-3 py-1.5 text-xs font-semibold text-gray-500 uppercase tracking-wide">{r.heading}</div>
        ) : (
          <div key={i} className={`flex justify-between px-3 py-1.5 text-sm border-t border-gray-100 ${r.muted ? 'text-gray-400 italic' : 'text-gray-700'}`}>
            <span>{r.label}</span>
            <Amt value={r.amount} />
          </div>
        ))}
        <div className="flex justify-between px-3 py-2 border-t-2 border-gray-300 text-sm font-bold text-gray-900 bg-gray-50 rounded-b">
          <span>Total</span>
          <Amt value={total} bold />
        </div>
      </div>
    </div>
  );

  return (
    <div>
      <div className="page-header">
        <h1 className="page-title">Balance Sheet</h1>
        <button onClick={download} disabled={busy} className="btn btn-secondary flex items-center gap-2 disabled:opacity-50">
          <FileDown size={16} /> {busy ? 'Generating…' : 'Download PDF'}
        </button>
      </div>

      <div className="card mb-6">
        <div className="flex flex-wrap gap-4 items-end">
          <div>
            <label className="label">As on Date</label>
            <input className="input" type="date" value={asOn} onChange={e => setAsOn(e.target.value)} />
          </div>
        </div>
      </div>

      <div className="card">
        <div id="bs-print-area" style={{ background: '#fff' }}>
          <div className="text-center mb-5">
            <h2 className="text-lg font-bold text-gray-900">{business}</h2>
            <p className="text-sm text-gray-600">Balance Sheet</p>
            <p className="text-xs text-gray-500">As on {formatDate(asOn)}</p>
          </div>

          <div className="flex flex-col md:flex-row gap-4">
            <Column title="Liabilities &amp; Capital" rows={leftRows} total={bs.leftTotal} />
            <Column title="Assets" rows={rightRows} total={bs.rightTotal} />
          </div>

          {bs.difference !== 0 && (
            <p className="text-[11px] text-amber-600 mt-3 text-center">
              A difference of {formatCurrency(Math.abs(bs.difference))} exists — usually from opening balances that don't self-tally. Shown as a reconciling line so the statement foots.
            </p>
          )}
          <p className="text-[10px] text-gray-400 mt-2 text-center">
            Cash/Bank &amp; stock valued at current book value. Generated on {formatDate(today())}.
          </p>
        </div>
      </div>
    </div>
  );
}
